import React, { useEffect, useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faSave, faTags, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useServiceCategoryStore } from '../../../features/services/store/useServiceCategoryStore.ts';
import type { CreateServiceCategoryRequest, ServiceCategoryResponse, UpdateServiceCategoryRequest } from '../../../api/generated';

const ServiceCategoriesPage: React.FC = () => {
  const breadcrumb = [{ label: 'Nastavení' }, { label: 'Kategorie služeb' }];

  // Service categories store
  const { categories, loading: categoriesLoading, error: categoriesError, loadCategories, createCategory, updateCategory, deleteCategory } = useServiceCategoryStore();

  // Category management state
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState<CreateServiceCategoryRequest>({
    name: '',
    color: '#3B82F6',
    description: '',
    icon: '',
    sortOrder: 0,
  });
  const [editCategory, setEditCategory] = useState<UpdateServiceCategoryRequest>({
    name: '',
    color: '#3B82F6',
    description: '',
    icon: '',
    isActive: true,
    sortOrder: 0,
  });

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Funkce pro normalizaci barvy
  const normalizeColor = (color: string | undefined): string => {
    if (!color) return '#3B82F6'; // Default blue

    // Pokud už má #, vrátit jak je
    if (color.startsWith('#')) {
      return color;
    }

    // Pokud je to hex bez #, přidat #
    if (/^[0-9A-Fa-f]{6}$/.test(color)) {
      return `#${color}`;
    }

    // Pokud je to RGB, převést na hex
    if (color.startsWith('rgb')) {
      // Jednoduchý RGB to hex converter
      const rgb = color.match(/\d+/g);
      if (rgb && rgb.length === 3) {
        const r = parseInt(rgb[0]);
        const g = parseInt(rgb[1]);
        const b = parseInt(rgb[2]);
        return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
      }
    }

    // Fallback
    return color.startsWith('#') ? color : `#${color}`;
  };

  // Category management functions
  const handleCreateCategory = async () => {
    try {
      await createCategory(newCategory);
      setNewCategory({
        name: '',
        color: '#3B82F6',
        description: '',
        icon: '',
        sortOrder: 0,
      });
      setShowAddCategory(false);
    } catch (error) {
      console.error('Chyba při vytváření kategorie:', error);
    }
  };

  const handleUpdateCategory = async (id: string) => {
    try {
      await updateCategory(id, editCategory);
      setEditingCategory(null);
    } catch (error) {
      console.error('Chyba při úpravě kategorie:', error);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (window.confirm('Opravdu chcete smazat tuto kategorii?')) {
      try {
        await deleteCategory(id);
      } catch (error) {
        console.error('Chyba při mazání kategorie:', error);
      }
    }
  };

  const startEditCategory = (category: ServiceCategoryResponse) => {
    setEditCategory({
      name: category.name,
      color: category.color,
      description: category.description || '',
      icon: category.icon || '',
      isActive: category.isActive,
      sortOrder: category.sortOrder,
    });
    setEditingCategory(category.id);
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Kategorie služeb" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Kategorie služeb</h1>
                <p className="text-gray-600 mt-1">Spravujte kategorie pro organizaci služeb</p>
                {/* Statistics */}
                <div className="flex items-center space-x-4 mt-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">
                      Aktivní: <span className="font-medium text-gray-900">{categories.filter(cat => cat.isActive).length}</span>
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <span className="text-sm text-gray-600">
                      Celkem: <span className="font-medium text-gray-900">{categories.length}</span>
                    </span>
                  </div>
                </div>
              </div>
              <button onClick={() => setShowAddCategory(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Nová kategorie
              </button>
            </div>
          </section>

          {/* Error State */}
          {categoriesError && (
            <section className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-red-600">
                <p className="font-medium">Chyba při načítání kategorií:</p>
                <p className="text-sm mt-1">{categoriesError}</p>
              </div>
            </section>
          )}

          {/* Loading State */}
          {categoriesLoading && (
            <section className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-center py-8">
                <div className="text-gray-500">Načítání kategorií...</div>
              </div>
            </section>
          )}

          {/* Categories Grid */}
          {!categoriesLoading && !categoriesError && (
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map(category => {
                const isEditing = editingCategory === category.id;

                return (
                  <div key={category.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    {isEditing ? (
                      // Edit Mode
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Název</label>
                          <input
                            type="text"
                            value={editCategory.name}
                            onChange={e => setEditCategory(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Barva</label>
                          <input
                            type="color"
                            value={editCategory.color}
                            onChange={e => setEditCategory(prev => ({ ...prev, color: e.target.value }))}
                            className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Popis</label>
                          <textarea
                            value={editCategory.description}
                            onChange={e => setEditCategory(prev => ({ ...prev, description: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            rows={2}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Pořadí</label>
                          <input
                            type="number"
                            value={editCategory.sortOrder}
                            onChange={e => setEditCategory(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <button onClick={() => handleUpdateCategory(category.id)} className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700">
                            <FontAwesomeIcon icon={faSave} className="mr-1" />
                            Uložit
                          </button>
                          <button onClick={() => setEditingCategory(null)} className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600">
                            <FontAwesomeIcon icon={faTimes} className="mr-1" />
                            Zrušit
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div
                              className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                              style={{
                                backgroundColor: normalizeColor(category.color),
                              }}
                            />
                            <h4 className="font-semibold text-gray-900">{category.name}</h4>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button onClick={() => startEditCategory(category)} className="text-gray-400 hover:text-blue-600 p-1" title="Upravit">
                              <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteCategory(category.id)} className="text-gray-400 hover:text-red-600 p-1" title="Smazat">
                              <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {category.description && <p className="text-sm text-gray-600">{category.description}</p>}

                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs ${category.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {category.isActive ? 'Aktivní' : 'Neaktivní'}
                            </span>
                            <span className="text-gray-500">Pořadí: {category.sortOrder}</span>
                          </div>
                          <div className="text-xs text-gray-400">ID: {category.id.slice(0, 8)}...</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          )}

          {/* No Categories Message */}
          {!categoriesLoading && !categoriesError && categories.length === 0 && (
            <section className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-center py-12">
                <FontAwesomeIcon icon={faTags} className="w-16 h-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné kategorie</h3>
                <p className="text-gray-500 mb-4">Zatím nemáte žádné kategorie služeb.</p>
                <div className="text-sm text-gray-400 mb-4">Aktivní: 0 | Celkem: 0</div>
                <button onClick={() => setShowAddCategory(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Vytvořit první kategorii
                </button>
              </div>
            </section>
          )}

          {/* Add Category Modal */}
          {showAddCategory && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Nová kategorie</h3>
                  <button onClick={() => setShowAddCategory(false)} className="text-gray-400 hover:text-gray-600">
                    <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Název</label>
                    <input
                      type="text"
                      value={newCategory.name}
                      onChange={e => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Např. Úprava vlasů"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Barva</label>
                    <input
                      type="color"
                      value={newCategory.color}
                      onChange={e => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
                      className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Popis</label>
                    <textarea
                      value={newCategory.description}
                      onChange={e => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows={3}
                      placeholder="Popis kategorie..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pořadí</label>
                    <input
                      type="number"
                      value={newCategory.sortOrder}
                      onChange={e => setNewCategory(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6">
                  <button onClick={() => setShowAddCategory(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                    Zrušit
                  </button>
                  <button onClick={handleCreateCategory} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                    Vytvořit kategorii
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceCategoriesPage;
